/**
 * Contact Utility Functions
 *
 * Custom utilities for creating vCards and handling contact data
 */

import { formatPhoneForWhatsApp } from './formatters';
import { generateNotePrefill, gateVenueMetadata } from './locationFormatters';
import { drawScaledCanvas } from './imageScale';

/**
 * Get the first name (first whitespace-delimited token) from a full name.
 * @param {string} fullName
 * @returns {string} the first name, or '' when the input is empty
 */
export const firstNameOf = (fullName) => (fullName ? fullName.trim().split(/\s+/)[0] : '');

/** The busy line (Mark, 2026-10-21): the person's first name — the busy screen and the code page's sheet */
export const busyTitle = (name) =>
  `${firstNameOf(name) || 'Someone'}'s connecting with someone else`;

/** The profile's name has a surname: the share sheets' full-name row exists only then */
export const hasTwoNames = (profile) => (profile?.name || '').trim().split(/\s+/).length > 1;

/**
 * The per-connection sharing-ledger heading, used wherever a surface answers
 * "what does this person have of mine?" — the Profile detail page's shared
 * list and the Anon share overlay reached by tapping your own contact card.
 *
 * ONE function because the degradation is the interesting part: a name that
 * hasn't landed yet becomes "them", never an empty tail ("Shared with "). Both
 * call sites hand-rolled the same `|| 'them'` on 2026-08-20 and were merged
 * here at that branch's /go:review — the second copy is where a future change
 * to the fallback silently applies to only one screen.
 *
 * Takes a FULL name and reduces it; callers should not pre-split.
 */
export const sharedWithLabel = (fullName) => `Shared with ${firstNameOf(fullName) || 'them'}`;

// vCard 3.0 text-value escaping (RFC 2426): backslash, newlines, semicolon,
// comma. Conditional rewrites only — clean values stay byte-identical, so the
// device-proven N/FN/ORG shapes are untouched unless a special char appears.
// An unescaped ; shifts structured fields; a bare newline injects a line
// upstream of PHOTO — the one text mechanism that can derail the whole card.
const escapeVCardText = (value) =>
  String(value)
    .replace(/\\/g, '\\\\')
    .replace(/\r\n?/g, '\n')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');

// Append one property line with the VALUE escaped — the writer's DEFAULT.
// Every free-text value goes through here; raw `${...}` interpolation into the
// card is the exception that needs a reason (N composes escaped components,
// ORG carries a structural trailing separator, PHOTO/REV are generated data).
const vLine = (prop, value) => `${prop}:${escapeVCardText(value)}\n`;

/**
 * Create a vCard format string
 * @param {Object} contactInfo - The contact information
 * @param {string} photoBase64 - Optional base64 encoded photo data
 * @returns {string} A vCard format string
 */
export const createVCard = (contactInfo, photoBase64) => {
  const {
    name,
    // Primary contact fields
    phone,
    email,
    instagram,
    whatsapp,
    linkedin,
    // Secondary contact fields
    phone2,
    email2,
    instagram2,
    whatsapp2,
    // Expanded contact types
    facebook,
    github,
    signal,
    snapchat,
    substack,
    telegram,
    tiktok,
    twitch,
    twitter,
    youtube,
    // Work/About fields
    title,
    company,
    // The owner's per-connection nickname (viewer-private descriptor)
    nickname,
    note,
    other,
    otherLabel,
  } = contactInfo;

  // Start with standard vCard format
  let vCard = 'BEGIN:VCARD\nVERSION:3.0\n';

  // Add structured name field (N:) FIRST - iOS requires this for proper contact formatting
  // Format: N:LastName;FirstName;MiddleName;Prefix;Suffix
  // iOS uses the N: field as the primary name source
  let nameParts = name ? name.split(' ') : [];
  let firstName = escapeVCardText(nameParts[0] || '');
  let lastName = escapeVCardText(nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

  // If only one name part, put it in first name position (more natural for iOS)
  if (nameParts.length === 1) {
    vCard += `N:;${firstName};;;\n`;
  } else {
    vCard += `N:${lastName};${firstName};;;\n`;
  }

  // Add formatted name (display name)
  vCard += vLine('FN', name || '');

  // Primary phone
  if (phone) {
    vCard += vLine('TEL;TYPE=CELL', phone);
  }
  // Secondary phone
  if (phone2) {
    vCard += vLine('TEL;TYPE=CELL', phone2);
  }

  // Primary email
  if (email) {
    vCard += vLine('EMAIL', email);
  }
  // Secondary email
  if (email2) {
    vCard += vLine('EMAIL', email2);
  }

  // Social Profiles - iOS natively supports these as pre-set selections
  // Use just the identifier (username or phone), NOT full URLs
  // Full URLs cause iOS to create custom service entries instead of using native selections

  if (instagram) {
    // Instagram needs @ prefix and proper case for iOS to recognize it
    const instaUsername = instagram.startsWith('@') ? instagram : `@${instagram}`;
    vCard += vLine('X-SOCIALPROFILE;TYPE=Instagram', instaUsername);
  }

  if (whatsapp) {
    // WhatsApp needs + prefix (E.164) and proper case for iOS to recognize it
    const whatsappNumber = '+' + formatPhoneForWhatsApp(whatsapp);
    vCard += vLine('X-SOCIALPROFILE;TYPE=WhatsApp', whatsappNumber);
  }

  if (linkedin) {
    // LinkedIn - proper case for iOS native recognition
    vCard += vLine('X-SOCIALPROFILE;TYPE=LinkedIn', linkedin);
  }

  // Secondary social profiles
  if (instagram2) {
    const instaUsername2 = instagram2.startsWith('@') ? instagram2 : `@${instagram2}`;
    vCard += vLine('X-SOCIALPROFILE;TYPE=Instagram', instaUsername2);
  }

  if (whatsapp2) {
    const whatsappNumber2 = '+' + formatPhoneForWhatsApp(whatsapp2);
    vCard += vLine('X-SOCIALPROFILE;TYPE=WhatsApp', whatsappNumber2);
  }

  // Expanded contact types
  if (facebook) {
    vCard += vLine('X-SOCIALPROFILE;TYPE=Facebook', facebook);
  }

  if (github) {
    vCard += vLine('X-SOCIALPROFILE;TYPE=GitHub', github);
  }

  if (signal) {
    // Signal uses phone number in E.164 format
    const signalNumber = signal.startsWith('+') ? signal : `+${signal.replace(/\D/g, '')}`;
    vCard += vLine('X-SOCIALPROFILE;TYPE=Signal', signalNumber);
  }

  if (snapchat) {
    vCard += vLine('X-SOCIALPROFILE;TYPE=Snapchat', snapchat);
  }

  if (substack) {
    // Substack is a subdomain, store as-is
    vCard += vLine('X-SOCIALPROFILE;TYPE=Substack', substack);
  }

  if (telegram) {
    vCard += vLine('X-SOCIALPROFILE;TYPE=Telegram', telegram);
  }

  if (tiktok) {
    // TikTok usernames - add @ prefix if not present
    const tiktokUsername = tiktok.startsWith('@') ? tiktok : `@${tiktok}`;
    vCard += vLine('X-SOCIALPROFILE;TYPE=TikTok', tiktokUsername);
  }

  if (twitch) {
    vCard += vLine('X-SOCIALPROFILE;TYPE=Twitch', twitch);
  }

  if (twitter) {
    // Twitter/X usernames - add @ prefix if not present
    const twitterUsername = twitter.startsWith('@') ? twitter : `@${twitter}`;
    vCard += vLine('X-SOCIALPROFILE;TYPE=Twitter', twitterUsername);
  }

  if (youtube) {
    vCard += vLine('X-SOCIALPROFILE;TYPE=YouTube', youtube);
  }

  // "Other" custom social profile - iOS will display as custom service
  if (other && otherLabel) {
    // Use the user's label as the TYPE - iOS will create a custom service entry.
    // TYPE is a vCard PARAMETER — backslash escaping doesn't apply there, so
    // structural chars in the free-text label are stripped; the value side
    // escapes normally (both were raw interpolation — found at /review).
    const safeLabel = String(otherLabel)
      .replace(/[;:,\r\n\\]/g, ' ')
      .trim();
    vCard += vLine(`X-SOCIALPROFILE;TYPE=${safeLabel || 'Other'}`, other);
  }

  // ORG carries the peer's shared COMPANY and nothing else (user ruling
  // 2026-08-20). It used to fall back to the owner's nickname, which was
  // defensible while that field held a short descriptor — iOS renders ORG above
  // the name, so "Coffee guy" read well there. The Note/Nickname merge ended
  // that: the field is now one free-text note, and a sentence rendered as a
  // company line looks broken (device: "COOL DUDE" above Harry).
  //
  // The note goes to NOTE instead — see below, where the branch that already
  // did this for the company-present case becomes unconditional.
  //
  // Still NEVER the vCard NICKNAME property \u2014 iOS can let it hijack the
  // contact's display name, depending on the per-device "Prefer Nicknames"
  // setting. That hazard is unchanged by this ruling.
  const orgValue = company;
  if (orgValue) {
    // Structured form with the trailing unit separator (ORG:Name;) — the shape
    // Apple Contacts itself exports. Hypothesis fix for an observed iOS quirk
    // (2026-07-19 device pass): bare ORG showed in the import preview but left
    // the Company field empty in the Create New Contact form. Verify on device.
    // Escaped: the nickname is free text — an unescaped ; , or newline here
    // shifts ORG's fields or injects a bare line upstream of PHOTO.
    vCard += `ORG:${escapeVCardText(orgValue)};\n`;
  }
  // Braille blank (U+2800) as TITLE fallback ONLY when title AND ORG are both
  // missing \u2014 prevents iOS showing "UNKNOWN" in the import preview (invisible
  // but recognized as data). This now fires MORE often: with the nickname no
  // longer falling back into ORG, a peer who shared no company leaves ORG empty.
  const titleValue = title || (orgValue ? '' : '\u2800');
  if (titleValue) {
    vCard += vLine('TITLE', titleValue);
  }

  // Handle the user's note - ensure it's a string
  let noteText = '';
  if (note !== undefined && note !== null) {
    if (typeof note === 'object' && note !== null) {
      // Avoid [object Object] — treat as empty
      noteText = '';
    } else {
      noteText = String(note).trim();
    }
  }

  // The owner's note ALWAYS leads the NOTE, above the met-line — saved and
  // searchable in iOS Contacts. Was gated on `company &&` (i.e. only when the
  // company had taken ORG); unconditional since 2026-08-20, because the note no
  // longer goes to ORG in any case.
  if (nickname) {
    noteText = noteText ? `${nickname}\n\n${noteText}` : nickname;
  }

  // Add the note to the vCard if we have one
  if (noteText) {
    // Use plain vCard 3.0 NOTE with UTF-8 charset (no QUOTED-PRINTABLE)
    // vCard 3.0 supports UTF-8 natively — all modern iOS/Android parsers handle it
    vCard += vLine('NOTE;CHARSET=UTF-8', noteText);
  }

  // Only include photo if we have valid image data
  if (photoBase64 && typeof photoBase64 === 'string' && photoBase64.length > 100) {
    // Clean photo data of any invalid chars, whitespace or newlines that could corrupt vCard
    photoBase64 = photoBase64.replace(/[^A-Za-z0-9+/=]/g, '');

    // vCard 3.0 requires line folding at 75 chars — continuation lines start with a space.
    // Without folding, iOS silently ignores the photo on a 22K+ character line.
    const photoLine = 'PHOTO;TYPE=JPEG;ENCODING=b:' + photoBase64;
    const LINE_LEN = 75;
    let folded = photoLine.substring(0, LINE_LEN);
    for (let i = LINE_LEN; i < photoLine.length; i += LINE_LEN - 1) {
      folded += '\r\n ' + photoLine.substring(i, i + LINE_LEN - 1);
    }
    vCard += folded + '\n';
  } else if (photoBase64) {
    console.warn('Invalid photo data detected, skipping photo field');
  }

  // Add REV timestamp field - this is metadata not fake data, required by iOS
  const now = new Date();
  const timestamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
  vCard += `REV:${timestamp}\n`;

  vCard += 'END:VCARD';

  // vCard spec requires CRLF line endings. The photo folding already uses CRLF,
  // but the rest of the card uses LF. Normalize everything to CRLF to avoid
  // mixed line endings which cause iOS to silently ignore the photo.
  return vCard.replace(/\r\n/g, '\n').replace(/\n/g, '\r\n');
};

/**
 * Fetch an image and convert it to vCard-ready base64.
 *
 * The image is decoded and RE-ENCODED through canvas — never embedded raw.
 * This strips EXIF/ICC metadata and normalizes any source to a baseline JPEG
 * at a poster-friendly size: the byte shape iOS's contact renderer is proven
 * to accept (app-uploaded photos already get this via chatImageUpload; static
 * and legacy photos don't — device pass 2026-07-19, photos silently dropped
 * from the saved contact).
 *
 * @param {string} url - The image URL
 * @returns {Promise<string>} vCard-ready base64, or '' on any failure
 *   (the call site falls back to the generated initial avatar)
 */
export const imageUrlToBase64 = async (url) => {
  let objectUrl = null;
  try {
    const response = await fetch(url);

    if (!response.ok) {
      console.error('Fetch failed with status:', response.status);
      return '';
    }

    // Verify we received an image MIME type
    const contentType = response.headers.get('content-type');

    if (!contentType || !contentType.startsWith('image/')) {
      console.error('URL did not return an image:', contentType);
      return '';
    }

    const blob = await response.blob();

    // Decode via <img> (universal), then re-encode: longest side scaled to
    // 512px (up or down — a 200px source also renders too small in the iOS
    // contact poster), JPEG quality 0.85
    objectUrl = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = objectUrl;
    });

    const canvas = drawScaledCanvas(img, 512, { upscale: true });
    const base64String = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];

    // Basic validation
    if (!base64String || base64String.length < 100) {
      console.error('Canvas re-encode resulted in invalid or too short string');
      return '';
    }

    // Ensure there are no newlines or spaces that could corrupt the vCard
    return base64String.replace(/[\r\n\s]/g, '');
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return '';
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Create a URL for a vCard using Blob
 * @param {string} vCardString - The vCard string
 * @returns {string} A Blob URL that can be used to download the vCard
 */
export const createVCardDataUri = (vCardString) => {
  // Use Blob instead of data URI to avoid encoding issues and security popups
  const blob = new Blob([vCardString], { type: 'text/vcard' });
  return URL.createObjectURL(blob);
};

/**
 * Generate a fallback avatar as base64 JPEG
 * Creates a colored circle with the user's initial
 * @param {string} name - The user's name (first letter used as initial)
 * @returns {string} Base64-encoded JPEG image data
 */
export const generateFallbackAvatar = (name) => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');

    // Choose a color based on the first letter of the name
    const initial = (name || '?').charAt(0).toUpperCase();
    const colors = ['#FF5C5C', '#5C9DFF', '#5CE1FF', '#5CFFA3', '#FFDE5C', '#FF5CF6'];
    const colorIndex = initial.charCodeAt(0) % colors.length;
    const bgColor = colors[colorIndex];

    // Draw background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 100px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initial, canvas.width / 2, canvas.height / 2);

    // Convert to base64 data URL - use JPEG format for iOS compatibility
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    let photoBase64 = dataUrl.split(',')[1];
    // Remove any whitespace or newlines that could corrupt the vCard
    photoBase64 = photoBase64.replace(/[\r\n\s]/g, '');
    return photoBase64;
  } catch (error) {
    console.error('Error creating fallback avatar:', error);
    return '';
  }
};

/**
 * Save contact to phone - consolidated vCard generation and download
 *
 * This is the single source of truth for all vCard save functionality.
 * Used by: ConnectionDetailPage (Anon + Profile detail), ManualAddDetailPage
 *
 * The vCard NOTE carries only the met-date/venue origin line (plus the
 * nickname when the peer's company occupies ORG — the Company rule in
 * createVCard). The user's app note deliberately does NOT flow to the vCard
 * (2026-07-18): the nickname is the descriptor that travels.
 *
 * @param {Object} options - The save options
 * @param {Object} options.userData - Contact data (profileData or anonData)
 * @param {string} options.chatRoomId - The chat room ID for this connection
 * @param {Object} options.connectionMetadata - Connection metadata, RAW — the
 *   viewer-dependent gating happens in here, not at call sites
 * @param {string} options.nickname - The owner's per-connection nickname (Company rule)
 * @param {boolean} options.viewerIsProfile - the ONE viewer axis. Anon viewers
 *   (false) get the page-parity vCard: venue-gated metadata, friendly time, no
 *   street address, and no title/company (above-the-Save-button rule, decision
 *   2026-07-19 — About sits below, so the nickname always owns ORG there).
 *   Profile viewers keep exact time, full address, and work fields.
 * @param {Function} options.updateContactSavedStatus - Context method to mark contact as saved
 * @returns {Promise<boolean>} True if save was successful
 */
export const saveContactToPhone = async ({
  userData,
  chatRoomId,
  connectionMetadata,
  nickname,
  viewerIsProfile = true,
  updateContactSavedStatus,
}) => {
  try {
    if (!userData || !userData.name) {
      console.error('[saveContactToPhone] No valid userData provided');
      return false;
    }

    const sharedFields = userData.sharedFields || ['name', 'photoUrl'];

    // Convert photo to base64 if available and photoUrl is shared
    let photoBase64 = '';
    if (userData.photoUrl && sharedFields.includes('photoUrl')) {
      try {
        // Fix CORS issues by using the same domain
        let photoUrl = userData.photoUrl;

        if (photoUrl.startsWith('http')) {
          // Don't rewrite Supabase Storage URLs - they have CORS enabled
          const isSupabaseStorage = photoUrl.includes('.supabase.co/storage/');

          if (!isSupabaseStorage) {
            try {
              // Only rewrite local photos that were mistakenly absolute
              const urlObj = new URL(photoUrl);
              const pathPart = urlObj.pathname;
              photoUrl = window.location.origin + pathPart;
            } catch (urlError) {
              console.error('[saveContactToPhone] Error parsing photo URL:', urlError);
            }
          }
        }

        photoBase64 = await imageUrlToBase64(photoUrl);
      } catch (error) {
        console.error('[saveContactToPhone] Error converting photo to base64:', error);
      }

      // Fallback to generated avatar if photo fetch failed
      if (!photoBase64) {
        photoBase64 = generateFallbackAvatar(userData.name);
      }
    }

    // The vCard note is the met-date/venue origin line only, formatted exactly
    // as the viewer's WE MET card (user note text stays in the app; createVCard
    // prepends the nickname when company wins ORG). Anon viewers get the GATED
    // metadata — an unconfirmed venue must never reach the phone's contact
    // record when the page itself hides it (contract #7 parity).
    const noteMetadata = viewerIsProfile
      ? connectionMetadata
      : gateVenueMetadata(connectionMetadata);
    const dateLocationContext = generateNotePrefill(noteMetadata, {
      // Exact time for every viewer (device pass 1, 2026-07-27): the Anon
      // detail card dropped friendlyTime rounding, and this note mirrors that
      // card — the two can never drift (above-the-button rule).
      includeAddress: viewerIsProfile,
    });

    // Create vCard from userData, respecting sharedFields
    const vcard = createVCard(
      {
        // Always include name
        name: userData.name,

        // Primary contact fields - only include if in sharedFields
        phone: sharedFields.includes('phone') ? userData.phone : undefined,
        email: sharedFields.includes('email') ? userData.email : undefined,
        instagram: sharedFields.includes('instagram') ? userData.instagram : undefined,
        whatsapp: sharedFields.includes('whatsapp') ? userData.whatsapp : undefined,
        linkedin: sharedFields.includes('linkedin') ? userData.linkedin : undefined,

        // Secondary contact fields
        phone2: sharedFields.includes('phone2') ? userData.phone2 : undefined,
        email2: sharedFields.includes('email2') ? userData.email2 : undefined,
        instagram2: sharedFields.includes('instagram2') ? userData.instagram2 : undefined,
        whatsapp2: sharedFields.includes('whatsapp2') ? userData.whatsapp2 : undefined,

        // Expanded contact types
        facebook: sharedFields.includes('facebook') ? userData.facebook : undefined,
        github: sharedFields.includes('github') ? userData.github : undefined,
        signal: sharedFields.includes('signal') ? userData.signal : undefined,
        snapchat: sharedFields.includes('snapchat') ? userData.snapchat : undefined,
        substack: sharedFields.includes('substack') ? userData.substack : undefined,
        telegram: sharedFields.includes('telegram') ? userData.telegram : undefined,
        tiktok: sharedFields.includes('tiktok') ? userData.tiktok : undefined,
        twitch: sharedFields.includes('twitch') ? userData.twitch : undefined,
        twitter: sharedFields.includes('twitter') ? userData.twitter : undefined,
        youtube: sharedFields.includes('youtube') ? userData.youtube : undefined,

        // "Other" custom contact - will be added as X-SOCIALPROFILE
        other: sharedFields.includes('other') ? userData.other : undefined,
        otherLabel: sharedFields.includes('other') ? userData.otherLabel : undefined,

        // Work fields - included if 'aboutMe' is in sharedFields (aboutMe is the bundle name)
        // AND the viewer's layout saves them (above-the-button rule)
        // Note: Profile data uses 'role' and 'organization', vCard expects 'title' and 'company'
        title: viewerIsProfile && sharedFields.includes('aboutMe') ? userData.role : undefined,
        company:
          viewerIsProfile && sharedFields.includes('aboutMe') ? userData.organization : undefined,

        // The owner's nickname — ORG when no shared company, else leads the NOTE
        nickname: nickname || undefined,

        // Met-date/venue origin line for the vCard note
        note: dateLocationContext || '',
      },
      photoBase64
    );

    // Create blob URL for download
    const dataUri = createVCardDataUri(vcard);

    // Create and trigger download link
    const downloadLink = document.createElement('a');
    downloadLink.href = dataUri;
    downloadLink.download = `${userData.name || 'contact'}.vcf`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    // Mark contact as saved to hide "Save to contacts" button on detail page
    if (updateContactSavedStatus) {
      await updateContactSavedStatus(chatRoomId, true);
    }

    // Clean up the Blob URL to prevent memory leaks
    setTimeout(() => {
      URL.revokeObjectURL(dataUri);
    }, 1000);

    return true;
  } catch (error) {
    console.error('[saveContactToPhone] Error saving contact:', error);
    return false;
  }
};
